import { useEffect, useRef, useState } from "react";
import { formatCurrency } from "common/utils/format/number";

type ProductPricingProps = {
  isLoading: boolean;
  salePrice: number;
  savings: number;
  shirtCount: number;
};

export function ProductPricing({ isLoading, salePrice, savings, shirtCount }: ProductPricingProps) {
  const [highlighted, setHighlighted] = useState(false);
  const prevSalePrice = useRef(salePrice);

  useEffect(() => {
    if (prevSalePrice.current !== salePrice) {
      setHighlighted(true);
      const timeout = setTimeout(() => setHighlighted(false), 1000);
      prevSalePrice.current = salePrice;
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [salePrice]);

  if (shirtCount <= 0) return null;

  // Calculate average price per shirt
  const avgSalePrice = shirtCount > 0 ? salePrice / shirtCount : 0;

  if (isLoading) {
    return (
      <div className="flex w-full min-w-0 animate-pulse flex-col items-end lg:w-auto xl:mx-6">
        {/* Loading skeleton for total price */}
        <div className="flex flex-col items-end gap-1">
          <span className="inline-block h-8 w-32 rounded bg-gray-200" />
        </div>
        {/* Loading skeleton for per-item price */}
        <div className="mt-2 flex flex-col items-end gap-2 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2">
            <span className="inline-block h-6 w-20 rounded bg-gray-200" />
            <span className="inline-block h-5 w-24 rounded bg-gray-200" />
          </div>
        </div>
      </div>
    );
  }

  // Winner variant: Clean pricing with green savings badge
  return (
    <div className="flex w-full min-w-0 flex-col items-end lg:w-auto xl:mx-6">
      <div className="flex w-full flex-col items-end">
        <div className="flex w-full items-center justify-between lg:justify-end">
          {shirtCount ? <p className="text-sm font-medium lg:hidden">Subtotal:</p> : null}
          <div
            className="mb-1 flex items-center justify-end gap-1.5"
            data-testid="product-pricing-total"
          >
            <span
              className={`text-xl font-semibold text-gray-900 transition-colors duration-300 ${
                highlighted ? "animate-pulse" : ""
              }`}
            >
              {formatCurrency(salePrice)} total
            </span>
            {savings > 0 && (
              <span className="rounded-sm bg-green-700 px-1 py-0.5 text-xs font-medium text-white">
                {formatCurrency(savings)} Saved
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 whitespace-nowrap">
          <div className="rounded bg-blue-50 px-0.5">
            <span className="text-base font-semibold text-blue-700">
              {formatCurrency(avgSalePrice)} each
            </span>
          </div>
          <span className="text-sm font-medium text-gray-900">for {shirtCount} custom shirts</span>
        </div>
      </div>
    </div>
  );
}
